// src/utils/generateCalendarPdf.js
import jsPDF from "jspdf";

/**
 * Bina struktur kalendar bulanan (6 minggu × 7 hari),
 * bermula dengan Isnin.
 */
const buildMonthlyCalendar = (viewDate) => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth(); // 0-11

    const firstOfMonth = new Date(year, month, 1);

    // Sunday=0 → Monday=0, ..., Saturday=6
    const jsDayToMondayIndex = (jsDay) => (jsDay + 6) % 7;
    const startOffset = jsDayToMondayIndex(firstOfMonth.getDay());

    const weeks = [];
    let currentDay = 1 - startOffset;

    for (let row = 0; row < 6; row++) {
        const week = [];
        for (let col = 0; col < 7; col++) {
            const dateObj = new Date(year, month, currentDay);
            week.push({
                date: dateObj,
                inMonth: dateObj.getMonth() === month,
            });
            currentDay++;
        }
        weeks.push(week);
    }

    return weeks;
};

/**
 * Key tarikh ikut masa tempatan, bukan UTC.
 * Contoh: "2025-12-19"
 */
const toLocalDateKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

/**
 * Load image dari URL → DataURL (base64) untuk jsPDF.
 * Kalau rounded === true, crop bulat (avatar ustaz).
 */
// const loadImageAsDataUrl = (url, rounded = false, format = "png") =>
//     new Promise((resolve) => {
//         if (!url) return resolve(null);

//         const img = new Image();
//         img.crossOrigin = "anonymous";

//         img.onload = () => {
//             const canvas = document.createElement("canvas");
//             const ctx = canvas.getContext("2d");

//             // ========== 1. AVATAR (rounded true) ==========
//             if (rounded) {
//                 const MAX_SIZE = 120;
//                 const scale = Math.min(MAX_SIZE / img.width, MAX_SIZE / img.height);

//                 const targetW = img.width * scale;
//                 const targetH = img.height * scale;

//                 canvas.width = targetW;
//                 canvas.height = targetH;

//                 ctx.drawImage(img, 0, 0, targetW, targetH);

//                 // bulatkan
//                 const size = Math.min(targetW, targetH);
//                 const cropCanvas = document.createElement("canvas");
//                 const cropCtx = cropCanvas.getContext("2d");

//                 cropCanvas.width = size;
//                 cropCanvas.height = size;

//                 cropCtx.beginPath();
//                 cropCtx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
//                 cropCtx.clip();
//                 cropCtx.drawImage(canvas, 0, 0, size, size);

//                 return resolve(cropCanvas.toDataURL("image/png")); // keep transparent
//             }

//             // ========== 2. LOGO MASJID (preserve PNG transparency) ==========
//             if (format === "logo") {
//                 const MAX_W = 200;
//                 const scale = Math.min(MAX_W / img.width, 1);

//                 const targetW = img.width * scale;
//                 const targetH = img.height * scale;

//                 canvas.width = targetW;
//                 canvas.height = targetH;
//                 ctx.drawImage(img, 0, 0, targetW, targetH);

//                 return resolve(canvas.toDataURL("image/png")); // DO NOT COMPRESS
//             }

//             // ========== 3. POSTER KULIAH (resize + JPEG compress) ==========
//             {
//                 const MAX_W = 350;
//                 const scale = Math.min(MAX_W / img.width, 1);

//                 const targetW = img.width * scale;
//                 const targetH = img.height * scale;

//                 canvas.width = targetW;
//                 canvas.height = targetH;
//                 ctx.drawImage(img, 0, 0, targetW, targetH);

//                 // compress strongly
//                 return resolve(canvas.toDataURL("image/jpeg", 0.55));
//             }
//         };



//         img.onerror = () => resolve(null);
//         img.src = url;
//     });



const safeBustUrl = (url) => {
    if (/^(data:|blob:)/i.test(url)) return url;
    try {
        const u = new URL(url);
        u.searchParams.set("_cb", String(Date.now())); // elak 304/cache pelik
        return u.toString();
    } catch {
        // kalau URL bukan valid untuk new URL (rare)
        return url + (url.includes("?") ? "&" : "?") + `_cb=${Date.now()}`;
    }
};

const fetchBlob = async (url) => {
    const busted = safeBustUrl(url);
    const res = await fetch(busted, { mode: "cors", cache: "no-store" });
    console.log("[fetchBlob]", res.status, res.headers.get("content-type"), busted);

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    if (!blob || blob.size === 0) throw new Error("Empty blob");
    return blob;
};

/**
 * Load image dari URL → DataURL (base64) untuk jsPDF.
 * rounded: true => crop bulat (avatar)
 * format:
 *  - "logo"  => PNG (preserve transparent)
 *  - lain    => JPEG compress (poster)
 */
const loadHtmlImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("HTMLImage decode fail"));
        img.src = src;
    });

const loadImageAsDataUrl = async (url, rounded = false, format = "png") => {
    if (!url) return null;

    try {
        let drawable = null; // ImageBitmap atau HTMLImageElement
        let revokeUrl = null;

        // ✅ kalau data/blob: jangan fetch, terus decode guna Image
        if (/^(data:|blob:)/i.test(url)) {
            drawable = await loadHtmlImage(url);
        } else {
            const blob = await fetchBlob(url);

            // 1) cuba strict decode (laju)
            try {
                drawable = await createImageBitmap(blob);
            } catch {
                // 2) fallback paling stabil: buat blob URL → Image decode
                revokeUrl = URL.createObjectURL(blob);
                drawable = await loadHtmlImage(revokeUrl);
            }
        }

        const w0 = drawable.width || drawable.naturalWidth;
        const h0 = drawable.height || drawable.naturalHeight;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // ===== AVATAR ROUND =====
        if (rounded) {
            const MAX_SIZE = 120;
            const scale = Math.min(MAX_SIZE / w0, MAX_SIZE / h0);

            const targetW = Math.round(w0 * scale);
            const targetH = Math.round(h0 * scale);

            canvas.width = targetW;
            canvas.height = targetH;
            ctx.drawImage(drawable, 0, 0, targetW, targetH);

            const size = Math.min(targetW, targetH);
            const cropCanvas = document.createElement("canvas");
            const cropCtx = cropCanvas.getContext("2d");
            cropCanvas.width = size;
            cropCanvas.height = size;

            cropCtx.beginPath();
            cropCtx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
            cropCtx.clip();
            cropCtx.drawImage(canvas, 0, 0, size, size);

            if (revokeUrl) URL.revokeObjectURL(revokeUrl);
            return cropCanvas.toDataURL("image/png");
        }

        // ===== LOGO PNG =====
        if (format === "logo") {
            const MAX_W = 200;
            const scale = Math.min(MAX_W / w0, 1);
            const targetW = Math.round(w0 * scale);
            const targetH = Math.round(h0 * scale);

            canvas.width = targetW;
            canvas.height = targetH;
            ctx.drawImage(drawable, 0, 0, targetW, targetH);

            if (revokeUrl) URL.revokeObjectURL(revokeUrl);
            return canvas.toDataURL("image/png");
        }

        // ===== POSTER / RECT JPEG =====
        const MAX_W = 350;
        const scale = Math.min(MAX_W / w0, 1);
        const targetW = Math.round(w0 * scale);
        const targetH = Math.round(h0 * scale);

        canvas.width = targetW;
        canvas.height = targetH;

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, targetW, targetH);
        ctx.drawImage(drawable, 0, 0, targetW, targetH);

        if (revokeUrl) URL.revokeObjectURL(revokeUrl);
        return canvas.toDataURL("image/jpeg", 0.55);
    } catch (e) {
        console.warn("[PDF] loadImageAsDataUrl FAIL:", url, e?.message || e);
        return null;
    }
};





const normalizeHex = (hex, fallback = "#7A1777") => {
    if (!hex) return fallback;
    let h = String(hex).trim();
    if (!h.startsWith("#")) h = `#${h}`;
    // optional: validate ringkas
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h)) return fallback;
    return h;
};

const createPatternBackgroundImage = (
    pageWidth,
    pageHeight,
    startY = 0,
    endY = pageHeight,
    patternHex = "#7A1777" // ✅ tambah
) =>
    new Promise((resolve) => {
        const SCALE = 2;

        const canvas = document.createElement("canvas");
        const widthPx = Math.round(pageWidth * SCALE);
        const heightPx = Math.round(pageHeight * SCALE);
        canvas.width = widthPx;
        canvas.height = heightPx;

        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#E5E4E2";
        ctx.fillRect(0, 0, widthPx, heightPx);

        const startYPx = startY * SCALE;
        const endYPx = endY * SCALE;

        const radius = heightPx * 0.025;
        const gapFactor = 0.90;

        const baseHexHeight = Math.sqrt(3) * radius;
        const baseVertStep = baseHexHeight * gapFactor;
        const baseHorizStep = radius * 1.98 * gapFactor;

        const fill = normalizeHex(patternHex, "#7A1777"); // ✅ place 1

        let row = 0;
        let y = 0;

        while (y < heightPx) {
            if (y >= startYPx && y <= endYPx) {
                const shrinkFactor = Math.max(0.5, 1 - row * 0.02);
                const r = radius * shrinkFactor;

                const rowOffset = row % 2 === 0 ? 0 : baseHorizStep / 2;

                const totalCols = Math.floor(widthPx / baseHorizStep);
                const leftoverSpace = widthPx - totalCols * baseHorizStep;
                const baseX = leftoverSpace / 2;

                for (let col = -1; col <= totalCols + 1; col++) {
                    const cx = baseX + rowOffset + col * baseHorizStep;
                    const cy = y;

                    if (cx < -2 * radius || cx > widthPx + 2 * radius) continue;

                    ctx.beginPath();
                    for (let i = 0; i < 6; i++) {
                        const angle = (Math.PI / 3) * i + Math.PI / 6;
                        const px = cx + r * Math.cos(angle);
                        const py = cy + r * Math.sin(angle);
                        if (i === 0) ctx.moveTo(px, py);
                        else ctx.lineTo(px, py);
                    }
                    ctx.closePath();

                    ctx.fillStyle = fill; // ✅ place 1
                    ctx.fill();
                }
            }

            y += baseVertStep;
            row++;
        }

        resolve(canvas.toDataURL("image/jpeg", 0.7));
    });


const hexToRgb = (hex, fallback = { r: 122, g: 23, b: 119 }) => {
    if (!hex) return fallback;
    let h = String(hex).trim().replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6) return fallback;

    const n = parseInt(h, 16);
    if (Number.isNaN(n)) return fallback;

    return {
        r: (n >> 16) & 255,
        g: (n >> 8) & 255,
        b: n & 255,
    };
};

const getMosqueInitials = (name = "") => {
    const raw = String(name).trim();
    if (!raw) return "masjid";

    // token: split space, kekalkan "al-husna" sebagai satu token
    const words = raw
        .split(/\s+/)
        .map(w => w.replace(/[^A-Za-z0-9À-ÿ-]/g, "")) // allow hyphen
        .filter(Boolean);

    if (!words.length) return "masjid";

    const prefixes = new Set(["masjid", "surau", "musolla", "musholla", "mushola"]);
    const stop = new Set(["dan", "di", "bin", "binti", "bt", "b"]);

    const first = words[0].toLowerCase();
    const out = [];

    // ✅ kalau mula dengan masjid/surau/musolla -> ambil huruf dia (m/s/...)
    if (prefixes.has(first)) out.push(words[0][0].toLowerCase());

    // ambil initial perkataan lain
    for (let i = 1; i < words.length; i++) {
        const w = words[i];
        const lw = w.toLowerCase();

        // ✅ buang stopword yang EXACT sahaja (contoh: "al" sahaja kalau ada)
        if (stop.has(lw)) continue;

        // kalau token kosong lepas sanitize
        if (!w[0]) continue;

        out.push(w[0].toLowerCase());
    }

    return out.join("") || "masjid";
};







// const loadCoverImageAsDataUrl = (url, targetW, targetH, align = "center") =>
//     new Promise((resolve) => {
//         if (!url) return resolve(null);

//         const img = new Image();
//         img.crossOrigin = "anonymous";

//         img.onload = () => {
//             const canvas = document.createElement("canvas");
//             const ctx = canvas.getContext("2d");

//             canvas.width = targetW;
//             canvas.height = targetH;

//             // putih supaya JPEG bersih
//             ctx.fillStyle = "#ffffff";
//             ctx.fillRect(0, 0, targetW, targetH);

//             // 🔑 align ikut parameter
//             drawImageContain(ctx, img, targetW, targetH, 6, align);

//             resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.85), fmt: "JPEG" });
//         };

//         img.onerror = () => resolve(null);
//         img.src = url;
//     });

const loadCoverImageAsDataUrl = async (url, targetW, targetH, align = "center") => {
    if (!url) return null;

    try {
        const blob = await fetchBlob(url);
        const bmp = await createImageBitmap(blob);

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = targetW;
        canvas.height = targetH;

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, targetW, targetH);

        // draw contain guna bitmap (bmp pun ada width/height)
        drawImageContain(ctx, bmp, targetW, targetH, 6, align);

        return { dataUrl: canvas.toDataURL("image/jpeg", 0.85), fmt: "JPEG" };
    } catch (e) {
        console.warn("[PDF] loadCoverImageAsDataUrl FAIL:", url, e?.message || e);
        return null;
    }
};




const drawImageContain = (ctx, img, w, h, pad = 0, alignX = "center") => {
    const innerW = w - pad * 2;
    const innerH = h - pad * 2;

    const scale = Math.min(innerW / img.width, innerH / img.height);
    const dw = img.width * scale;
    const dh = img.height * scale;

    let dx = (w - dw) / 2;
    if (alignX === "left") dx = pad;
    if (alignX === "right") dx = w - dw - pad;

    const dy = (h - dh) / 2;

    ctx.drawImage(img, dx, dy, dw, dh);
};







const formatTime12h = (t) => {
    if (!t) return "";
    const m = String(t).trim().match(/^(\d{1,2}):(\d{2})/);
    if (!m) return String(t).trim();

    let hh = parseInt(m[1], 10);
    const mm = m[2];
    const ampm = hh >= 12 ? "pm" : "am";
    hh = hh % 12;
    if (hh === 0) hh = 12;
    return `${hh}:${mm} ${ampm}`;
};




const getKuliahTajuk = (ev) => {
    const p = ev?.extendedProps || {};
    return (
        (p.tajuk || p.tajukKuliah || p.topic || p.subtitle || p.programText || p.kuliahTitle || "")
            .toString()
            .trim()
    );
};


const ellipsizeOneLine = (pdf, text, maxWidth) => {
    const s = (text || "").toString().trim();
    if (!s) return "";

    if (pdf.getTextWidth(s) <= maxWidth) return s;

    const dots = "...";
    let lo = 0;
    let hi = s.length;

    // binary search panjang max yang muat
    while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        const candidate = s.slice(0, mid) + dots;
        if (pdf.getTextWidth(candidate) <= maxWidth) lo = mid;
        else hi = mid - 1;
    }

    return s.slice(0, lo) + dots;
};


const isKuliahEvent = (ev) => {
    const p = ev?.extendedProps || {};
    return !!(
        ev?.isKuliah ||                 // (kalau ada di root)
        p?.isKuliah ||                  // (biasanya di extendedProps)
        p?.eventField === "kuliah" ||   // (banyak sistem guna ini)
        p?.type === "kuliah"            // (fallback)
    );
};



/**
 * Generate PDF kalendar bulanan
 * - events: array dari FullCalendar (perlu .start dan extendedProps)
 * - currentDate: tarikh view semasa (Date)
 * - mosqueName: nama masjid untuk header
 * - logoUrl: optional, URL logo masjid
 */
export async function generateCalendarPdf({
    events,
    currentDate,
    mosqueName = "Masjid",
    logoUrl,
    etvColor = "#7A1777",
}) {
    const pdf = new jsPDF("landscape", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const theme = hexToRgb(etvColor);
    // ========= COLOR PALETTE =========
    // const pageBg = { r: 248, g: 247, b: 252 }; 
    const pageBg = { r: 255, g: 255, b: 255 };

    const headerBg = { r: 234, g: 228, b: 244 }; // bar nama hari
    const cellBgInMonth = { r: 255, g: 255, b: 255 }; // kotak hari aktif
    const cellBgOutMonth = { r: 244, g: 244, b: 244 }; // kotak hari luar bulan
    const fridayBg = { r: 233, g: 244, b: 236 }; // column Jumaat

    const margin = 32;
    const headerHeight = 40;
    const gridTop = margin + headerHeight + 6;
    const gridHeight = pageHeight - gridTop - margin;

    const cols = 7;

    // ========= PAGE BACKGROUND =========
    pdf.setFillColor(pageBg.r, pageBg.g, pageBg.b);
    pdf.rect(0, 0, pageWidth, pageHeight, "F");



    // ========= PAGE BACKGROUND (PATTERN IMAGE) =========
    // pattern bermula bawah header sahaja
    const patternStartY = gridTop - 30; // tweak jika perlu
    const patternEnd = pageHeight - 60;

    const patternBgDataUrl = await createPatternBackgroundImage(
        pageWidth,
        pageHeight,
        patternStartY,
        patternEnd,
        etvColor
    );

    pdf.addImage(patternBgDataUrl, "JPEG", 0, 0, pageWidth, pageHeight);



    // ========= HEADER (2 COLUMN) =========
    // ✅ place 2
    pdf.setFillColor(theme.r, theme.g, theme.b);
    pdf.setDrawColor(theme.r, theme.g, theme.b);
    pdf.rect(0, 0, pageWidth, gridTop - 10, "F");


    const monthLabel = currentDate.toLocaleDateString("ms-MY", {
        month: "long",
        year: "numeric",
    });

    const leftX = margin;
    const rightX = pageWidth - margin;

    const cellWidth = (pageWidth - margin * 2) / cols;




    // Logo (jika ada) di kiri
    let logoDataUrl = null;
    if (logoUrl) {
        logoDataUrl = await loadImageAsDataUrl(logoUrl, false, "logo");
    }
    const logoSize = 45;
    const logoY = margin - 24;

    if (logoDataUrl) {
        pdf.addImage(logoDataUrl, "PNG", leftX, logoY, logoSize, logoSize);
    }

    // Nama masjid di kiri (sebelah logo)
    pdf.setTextColor(255, 255, 255); // putih
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    const mosqueTextX = logoDataUrl ? leftX + logoSize + 10 : leftX;
    const mosqueTextY = margin + 6; // naikkan 10px
    pdf.text(mosqueName, mosqueTextX, mosqueTextY);

    pdf.setFontSize(12);
    pdf.text("Jadual Kuliah", rightX - 55, margin, { align: "right" });

    pdf.setFontSize(14);
    pdf.text(monthLabel, rightX - 55, margin + 15, { align: "right" });

    // === Kotak nombor bulan ===
    const monthNumber = String(currentDate.getMonth() + 1).padStart(2, "0");



    // Offset kotak berada selepas teks bulan
    const boxX = rightX - 45;
    const boxY = margin - 12;

    const boxW = 44;
    const boxH = 30;

    // Draw the rounded box
    pdf.setFillColor(255, 255, 255); // white box
    pdf.roundedRect(boxX, boxY, boxW, boxH, 4, 4, "F");

    // Text inside box
    // ✅ place 3
    pdf.setTextColor(theme.r, theme.g, theme.b);
    pdf.setFontSize(24);
    pdf.text(monthNumber, boxX + boxW / 2, boxY + boxH / 2 + 8, { align: "center" });

    // Reset
    pdf.setTextColor(255, 255, 255);




    // Nama hari (Isnin mula) – dengan background bar
    const dayNames = ["Isnin", "Selasa", "Rabu", "Khamis", "Jumaat", "Sabtu", "Ahad"];

    pdf.setFillColor(headerBg.r, headerBg.g, headerBg.b);
    pdf.rect(margin, gridTop - 20, cellWidth * cols, 20, "F");

    pdf.setFontSize(10);
    pdf.setTextColor(80);
    dayNames.forEach((name, i) => {
        const x = margin + i * cellWidth;
        const textX = x + 4;
        const textY = gridTop - 6;

        // --- Highlight Jumaat (index 4) ---
        if (i === 4) {
            pdf.setFillColor(208, 231, 214);
            pdf.rect(x, gridTop - 20, cellWidth, 22, "F");
        }

        pdf.setFontSize(10);
        pdf.setTextColor(50);
        pdf.text(name, textX, textY);
    });

    pdf.setTextColor(0);




    // ========= DATA & IMAGE PREP =========

    // Group events by tarikh (local date key)
    const eventsByDate = events.reduce((acc, ev) => {
        if (!ev.start) return acc;
        const key = toLocalDateKey(ev.start);
        if (!acc[key]) acc[key] = [];
        acc[key].push(ev);
        return acc;
    }, {});



    // Preload gambar untuk setiap event + simpan format
    // - poster: JPEG (rectangle)
    // - speaker avatar: PNG (rounded)
    // - speaker rect: JPEG (untuk single-event view)
    const imageCache = {}; // key -> { dataUrl, fmt }

    for (const ev of events) {
        const poster = ev.extendedProps?.posterImage;
        const speaker = ev.extendedProps?.speakerImage;

        if (poster && !imageCache[`poster:${poster}`]) {
            const dataUrl = await loadImageAsDataUrl(poster, false, "jpeg");
            if (dataUrl) imageCache[`poster:${poster}`] = { dataUrl, fmt: "JPEG" };
        }

        if (speaker && !imageCache[`avatar:${speaker}`]) {
            const dataUrl = await loadImageAsDataUrl(speaker, true, "png");
            if (dataUrl) imageCache[`avatar:${speaker}`] = { dataUrl, fmt: "PNG" };
        }

        if (speaker && !imageCache[`rect:${speaker}`]) {
            // guna loader yang sama (akan jadi JPEG), tapi bukan rounded
            const dataUrl = await loadImageAsDataUrl(speaker, false, "jpeg");
            if (dataUrl) imageCache[`rect:${speaker}`] = { dataUrl, fmt: "JPEG" };
        }
    }

    const getImageAsset = (ev, mode = "list") => {
        const poster = ev.extendedProps?.posterImage;
        const speaker = ev.extendedProps?.speakerImage;

        // mode "single": utamakan poster, kalau tiada guna speaker rect
        if (mode === "single") {
            if (poster && imageCache[`poster:${poster}`]) return imageCache[`poster:${poster}`];
            if (speaker && imageCache[`rect:${speaker}`]) return imageCache[`rect:${speaker}`];
            if (speaker && imageCache[`avatar:${speaker}`]) return imageCache[`avatar:${speaker}`];
            return null;
        }

        // mode "list" (layout lama): poster > avatar bulat
        if (poster && imageCache[`poster:${poster}`]) return imageCache[`poster:${poster}`];
        if (speaker && imageCache[`avatar:${speaker}`]) return imageCache[`avatar:${speaker}`];
        return null;
    };


    // ========= GRID =========

    const allWeeks = buildMonthlyCalendar(currentDate);

    // Buang minggu terakhir jika semua hari luar bulan → dynamic 5/6 row
    const weeks = [...allWeeks];
    while (
        weeks.length > 0 &&
        weeks[weeks.length - 1].every((day) => !day.inMonth)
    ) {
        weeks.pop();
    }

    const rows = weeks.length;
    const cellHeight = gridHeight / rows;

    // ===== SINGLE EVENT UI SETTINGS =====
    const singlePad = 4;
    const singleReserveBottom = 34;
    const singleImgW = Math.min(78, Math.round(cellWidth * 0.5));
    const singleImgH = Math.round(
        Math.max(46, Math.min(cellHeight - 18 - singleReserveBottom, 78))
    );


    // cari tarikh yang ada 1 event sahaja
    const singleDayKeys = [];
    for (const week of weeks) {
        for (const day of week) {
            if (!day.inMonth) continue;
            const key = toLocalDateKey(day.date);
            const list = (eventsByDate[key] || []);
            if (list.length === 1) singleDayKeys.push(key);
        }
    }

    const singleCoverCache = {}; // key -> {dataUrl, fmt}
    for (const key of singleDayKeys) {
        const ev = (eventsByDate[key] || [])[0];
        if (!ev) continue;

        const poster = ev.extendedProps?.posterImage || "";
        const speakerImg = ev.extendedProps?.speakerImage || "";
        const src = poster || speakerImg;

        const cacheKey = `${src}|${singleImgW}x${singleImgH}`;
        if (singleCoverCache[cacheKey]) continue;

        let asset = null;

        const isKuliah = isKuliahEvent(ev);

        if (isKuliah && !poster && speakerImg) {
            // ✅ SINGLE KULIAH: avatar ROUND (PNG transparent)
            const dataUrl = await loadImageAsDataUrl(speakerImg, true, "png");
            if (dataUrl) asset = { dataUrl, fmt: "PNG", kind: "avatar" };
        } else {
            // ✅ program/poster: kekal JPEG (ok ada putih)
            asset = await loadCoverImageAsDataUrl(src, singleImgW * 2, singleImgH * 2, "left");
            if (asset) asset.kind = "cover";
        }


        if (asset) singleCoverCache[cacheKey] = asset;

    }



    const lineHeight = 9;
    const maxLinesPerCell = 7;

    weeks.forEach((week, rowIdx) => {
        week.forEach((day, colIdx) => {
            const x = margin + colIdx * cellWidth;
            const y = gridTop + rowIdx * cellHeight;

            const isFriday = day.date.getDay() === 5; // 0=Ahad, 5=Jumaat

            // Background cell ikut sama ada dalam / luar bulan / Jumaat
            if (day.inMonth) {
                if (isFriday) {
                    pdf.setFillColor(fridayBg.r, fridayBg.g, fridayBg.b);
                } else {
                    pdf.setFillColor(cellBgInMonth.r, cellBgInMonth.g, cellBgInMonth.b);
                }
            } else {
                pdf.setFillColor(cellBgOutMonth.r, cellBgOutMonth.g, cellBgOutMonth.b);
            }
            pdf.rect(x, y, cellWidth, cellHeight, "F");

            // Border
            pdf.setDrawColor(241, 231, 241);
            pdf.rect(x, y, cellWidth, cellHeight);

            // nombor tarikh
            // nombor tarikh
            pdf.setFontSize(9);
            const dayNumber = day.date.getDate();
            if (!day.inMonth) {
                pdf.setTextColor(140); // hari luar bulan → kelabu
            } else {
                pdf.setTextColor(0);
            }
            pdf.text(String(dayNumber), x + cellWidth - 8, y + 12, {
                align: "right",
            });
            pdf.setTextColor(0);

            // --- Ambil event HANYA jika dalam bulan semasa ---
            let dayEvents = [];
            if (day.inMonth) {
                const key = toLocalDateKey(day.date);
                dayEvents = (eventsByDate[key] || []).slice().sort(
                    (a, b) => new Date(a.start) - new Date(b.start)
                );
            }

            pdf.setFontSize(7);


            let cursorY = y + 20;
            const baseX = x + 4;
            let linesUsed = 0;

            // ===== SPECIAL: kalau sehari hanya 1 event → gambar atas, teks bawah =====
            // ===== SINGLE EVENT: gambar besar + badge + nama =====
            if (dayEvents.length === 1) {
                const ev = dayEvents[0];
                const isKuliah = isKuliahEvent(ev);

                const poster = ev.extendedProps?.posterImage || "";
                const speakerImg = ev.extendedProps?.speakerImage || "";
                const src = poster || speakerImg;

                const pad = singlePad;

                // ✅ align left + petak radius (dari singleCoverCache |rounded)
                const imgX = Math.round(x + pad);
                const imgY = Math.round(y + 18);
                const imgW = Math.round(singleImgW);
                const imgH = Math.round(singleImgH);


                let imgBottomY = y + 18; // fallback

                if (src) {
                    const cacheKey = `${src}|${singleImgW}x${singleImgH}`; // ✅ sama
                    const asset = singleCoverCache[cacheKey];
                    if (asset?.dataUrl) {
                        if (asset.kind === "avatar") {
                            const s = Math.min(imgW, imgH);
                            pdf.addImage(asset.dataUrl, asset.fmt, imgX, imgY, s, s);
                            imgBottomY = imgY + s;
                        } else {
                            pdf.addImage(asset.dataUrl, asset.fmt, imgX, imgY, imgW, imgH);
                            imgBottomY = imgY + imgH;
                        }
                    }

                }

                const tX = x + pad;                    // ✅ align left
                const badgeWMax = cellWidth - pad * 2; // ✅ full usable width

                const title = (ev.title || "").trim() || "Aktiviti";
                const speaker = (ev.extendedProps?.speaker || ev.extendedProps?.speakerName || ev.extendedProps?.ustaz || "").trim();
                const tajuk = getKuliahTajuk(ev);
                const rawTime = (ev.extendedProps?.time || "").trim();
                const time12 = formatTime12h(rawTime);

                // ✅ rapat macam multi-event
                const tY = imgBottomY + 11;

                // ===== Badge (same style) =====
                const badgePaddingX = 3;
                pdf.setFontSize(7);
                const badgeLine = pdf.splitTextToSize(title, badgeWMax)[0];
                const textWidth = pdf.getTextWidth(badgeLine);
                const badgeW = Math.min(textWidth + badgePaddingX * 2, badgeWMax);
                const badgeH = 10;
                const r = 4;

                pdf.setFillColor(isKuliah ? 230 : 222, isKuliah ? 217 : 239, isKuliah ? 246 : 234);
                pdf.setDrawColor(255, 255, 255);
                pdf.setLineWidth(0);

                const path = [
                    [badgeW, 0],
                    [0, badgeH - r],
                    [-r, r],
                    [-badgeW + r, 0],
                ];
                pdf.lines(path, tX, tY - badgeH + 2, [1, 1], "F");

                pdf.setFontSize(6);
                pdf.setTextColor(60);
                pdf.text(badgeLine, tX + badgePaddingX, tY - 2);
                pdf.setTextColor(0);

                // ===== Text bawah badge =====
                let y2 = tY + 9;
                pdf.setFontSize(7);

                if (isKuliah) {
                    // 1) Nama ustaz (ganti masa)
                    if (speaker) {
                        pdf.setFont("helvetica", "bold");
                        pdf.setFontSize(8);

                        const speakerOneLine = ellipsizeOneLine(pdf, speaker, badgeWMax);
                        pdf.text(speakerOneLine, tX, y2);
                        y2 += 9;

                        pdf.setFont("helvetica", "normal");
                        pdf.setFontSize(7);
                    }


                    // 2) Tajuk kuliah (bawah nama ustaz)
                    if (tajuk) {
                        const metaRawSingle = (tajuk || time12 || "").trim();
                        if (metaRawSingle) {
                            pdf.setTextColor(80);

                            const metaOneLine = ellipsizeOneLine(pdf, metaRawSingle, badgeWMax);
                            pdf.text(metaOneLine, tX, y2);
                            y2 += 8;

                            pdf.setTextColor(0);
                        }

                    }
                } else {
                    // kekal program (masa)
                    if (time12) {
                        pdf.setTextColor(90);
                        pdf.text(time12, tX, y2);
                        pdf.setTextColor(0);
                    } else if (speaker) {
                        const lines = pdf.splitTextToSize(speaker, badgeWMax).slice(0, 2);
                        pdf.text(lines[0], tX, y2);
                        if (lines[1]) pdf.text(lines[1], tX, y2 + 8);
                    }
                }


            }

            else {
                // kekal code lama (multi-event)
                dayEvents.forEach((ev, index) => {

                    const isCrowded = dayEvents.length >= 3;
                    const maxLines = isCrowded ? 9 : maxLinesPerCell;
                    const tightLine = isCrowded ? 7.5 : 8;

                    if (linesUsed >= maxLines) return; // ✅

                    const isKuliah = isKuliahEvent(ev);
                    const imgAsset = getImageAsset(ev, "list");
                    const avatarSize = imgAsset?.dataUrl ? 14 : 0;

                    const gapBetweenEvents = dayEvents.length >= 3 ? 3 : 4;
                    if (index > 0) cursorY += gapBetweenEvents;

                    const avatarX = baseX;
                    const avatarY = cursorY + 2;
                    let textX = baseX;

                    if (imgAsset?.dataUrl) {
                        pdf.addImage(imgAsset.dataUrl, imgAsset.fmt, avatarX, avatarY, avatarSize, avatarSize);
                        textX = avatarX + avatarSize + 3;
                    }

                    let textY = cursorY + 4;
                    const availableWidth = cellWidth - (textX - x) - 4;
                    const linesRemaining = maxLines - linesUsed; // ✅

                    // ===== Title badge (1 line) =====
                    if (linesRemaining > 0) {
                        const rawTitle = ev.title || "";
                        if (rawTitle) {
                            const titleLines = pdf.splitTextToSize(rawTitle, availableWidth);
                            const line = titleLines[0];

                            const textWidth = pdf.getTextWidth(line);
                            const badgePaddingX = 2;
                            const badgeH = lineHeight - 1;
                            const badgeW = Math.min(textWidth + badgePaddingX * 2, availableWidth);

                            const badgeX = textX;
                            const badgeY = textY - lineHeight;

                            const r = 4;
                            pdf.setFillColor(isKuliah ? 230 : 222, isKuliah ? 217 : 239, isKuliah ? 246 : 234);
                            pdf.setDrawColor(255, 255, 255);
                            pdf.setLineWidth(0);

                            const path = [
                                [badgeW, 0],
                                [0, badgeH - r],
                                [-r, r],
                                [-badgeW + r, 0],
                            ];
                            pdf.lines(path, badgeX, badgeY, [1, 1], "F");

                            pdf.setFontSize(6);
                            pdf.setTextColor(60);
                            pdf.text(line, badgeX + badgePaddingX, textY - 3);
                            pdf.setTextColor(0);
                            pdf.setFontSize(8);

                            textY += 5;
                            linesUsed += 1;
                        }
                    }
                    // ===== Speaker =====
                    const speakerRaw =
                        (ev.extendedProps?.speaker ||
                            ev.extendedProps?.speakerName ||
                            ev.extendedProps?.ustaz ||
                            "").trim();

                    if (speakerRaw && linesUsed < maxLines) {
                        pdf.setFont("helvetica", "bold");
                        pdf.setFontSize(8);

                        const speakerOneLine = ellipsizeOneLine(pdf, speakerRaw, availableWidth);
                        pdf.text(speakerOneLine, textX, textY);

                        pdf.setFont("helvetica", "normal"); // reset
                        pdf.setFontSize(7.6);

                        textY += tightLine;   // advance 1 line sahaja
                        linesUsed++;

                        textY -= 1; // rapatkan sikit dengan meta (optional)
                    }


                    // ===== Meta =====
                    const time = (ev.extendedProps?.time || "").trim();
                    const time12 = formatTime12h(time);
                    const tajuk = getKuliahTajuk(ev);
                    const metaRaw = isKuliah ? (tajuk || time12 || "") : (time12 || "");

                    if (metaRaw && linesUsed < maxLines) { // ✅
                        pdf.setTextColor(90);
                        pdf.setFontSize(6.6);
                        const metaOneLine = ellipsizeOneLine(pdf, metaRaw, availableWidth);
                        pdf.text(metaOneLine, textX, textY);
                        textY += tightLine;
                        linesUsed++;
                        pdf.setTextColor(0);
                        pdf.setFontSize(7.6);
                    }

                    cursorY = textY;
                });
            }




        });
    });

    // Footer kecil
    pdf.setFontSize(8);
    pdf.setTextColor(80);

    const now = new Date();
    const dateStr = now.toLocaleDateString("ms-MY", {
        day: "2-digit",
        month: "short",
        year: "numeric"
    });

    // force AM/PM
    const timeStr = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
    });

    const footerText = `Jadual ini dijana secara automatik dari sistem etikaf.com pada ${dateStr} ${timeStr}`;

    pdf.text(
        footerText,
        pageWidth - margin,
        pageHeight - 18,
        { align: "right" }
    );

    const initials = getMosqueInitials(mosqueName);      // mai
    const mm = String(currentDate.getMonth() + 1).padStart(2, "0"); // 12
    const yy = String(currentDate.getFullYear()).slice(-2);         // 25

    const filename = `jadual-${initials}-${mm}${yy}.pdf`;
    pdf.save(filename);
}
